"use client"

import { useState, useEffect, useRef } from "react"
import styled, { keyframes } from "styled-components"
import { Heart, X, User, ArrowLeft, MessageSquare, Sparkles } from "lucide-react"
import { useTheme } from "../context/ThemeContext"
import { useAuth } from "../context/AuthContext"

const API_BASE = ''

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`

const slideOut = keyframes`
  to { transform: translateX(var(--slide-x)); opacity: 0; }
`

const matchPop = keyframes`
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); opacity: 1; }
`

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${p => p.theme.backgroundColor};
  padding: 20px;
  animation: ${fadeIn} 0.3s ease;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  width: 100%;
  max-width: 400px;
`

const BackBtn = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.textSecondary};
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: flex;
  &:hover { background: ${p => p.theme.hoverBackground}; }
`

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  color: ${p => p.theme.textPrimary};
  display: flex;
  align-items: center;
  gap: 8px;
`

const CardWrapper = styled.div`
  width: 100%;
  max-width: 380px;
  position: relative;
  min-height: 480px;
`

const Card = styled.div`
  background: ${p => p.theme.cardBackground};
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0,0,0,0.12);
  position: absolute;
  width: 100%;
  transition: transform 0.15s ease;
  cursor: grab;
  user-select: none;

  &.swiping {
    transition: none;
  }

  &.slide-out {
    animation: ${slideOut} 0.4s ease forwards;
  }
`

const CardImage = styled.div`
  width: 100%;
  height: 320px;
  background: ${p => p.bg ? `url(${p.bg}) center/cover` : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 120px;
    background: linear-gradient(transparent, rgba(0,0,0,0.7));
  }
`

const CardInfo = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px;
  z-index: 1;
`

const CardName = styled.h3`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: white;
`

const CardBio = styled.p`
  margin: 6px 0 0;
  font-size: 14px;
  color: rgba(255,255,255,0.85);
  line-height: 1.4;
`

const CardStats = styled.div`
  padding: 16px 20px;
  display: flex;
  gap: 20px;
  border-top: 1px solid ${p => p.theme.borderLight};
`

const Stat = styled.div`
  font-size: 13px;
  color: ${p => p.theme.textSecondary};
  span { font-weight: 600; color: ${p => p.theme.textPrimary}; }
`

const ActionBar = styled.div`
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-top: 24px;
`

const ActionBtn = styled.button`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: 2px solid ${p => p.color || '#e5e7eb'};
  background: ${p => p.theme.cardBackground};
  color: ${p => p.color || p.theme.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);

  &:hover {
    transform: scale(1.1);
    background: ${p => p.color || '#e5e7eb'};
    color: white;
  }

  &:active {
    transform: scale(0.95);
  }
`

const EmptyState = styled.div`
  text-align: center;
  color: ${p => p.theme.textMuted};
  padding: 40px;

  h3 { color: ${p => p.theme.textPrimary}; margin-bottom: 8px; }
  p { font-size: 14px; line-height: 1.5; }
`

const MatchOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease;
`

const MatchContent = styled.div`
  text-align: center;
  animation: ${matchPop} 0.5s ease;
`

const MatchTitle = styled.h2`
  color: #ff6b6b;
  font-size: 32px;
  margin: 0 0 8px;
`

const MatchSubtitle = styled.p`
  color: rgba(255,255,255,0.8);
  font-size: 16px;
  margin: 0 0 24px;
`

const MatchAvatars = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 24px;
`

const MatchAvatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${p => p.bg ? `url(${p.bg}) center/cover` : 'linear-gradient(135deg, #667eea, #764ba2)'};
  border: 3px solid #ff6b6b;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 32px;
`

const MatchBtn = styled.button`
  padding: 12px 32px;
  border-radius: 25px;
  border: none;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin: 0 8px;
  transition: transform 0.2s;
  &:hover { transform: scale(1.05); }
`

const SwipeIndicator = styled.div`
  position: absolute;
  top: 20px;
  ${p => p.side === 'left' ? 'left: 20px;' : 'right: 20px;'}
  padding: 8px 16px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 20px;
  border: 3px solid;
  transform: rotate(${p => p.side === 'left' ? '-15deg' : '15deg'});
  opacity: ${p => Math.min(Math.abs(p.offset) / 80, 1)};
  z-index: 10;
  ${p => p.side === 'left' 
    ? 'color: #ff4458; border-color: #ff4458;'
    : 'color: #00d46a; border-color: #00d46a;'}
`

export default function SwipeMatching({ onClose, onStartChat }) {
  const { theme } = useTheme()
  const { user } = useAuth()
  const [candidates, setCandidates] = useState([])
  const [currentIdx, setCurrentIdx] = useState(0)
  const [loading, setLoading] = useState(true)
  const [showMatch, setShowMatch] = useState(null)
  const [dragOffset, setDragOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const startX = useRef(0)
  const cardRef = useRef(null)

  useEffect(() => { loadCandidates() }, [])

  const loadCandidates = async () => {
    try {
      setLoading(true)
      const username = user?.username || user?.id
      const res = await fetch(`${API_BASE}/match/candidates`, {
        headers: { 'X-Username': username },
        credentials: 'include'
      })
      const data = await res.json()
      if (data.success) setCandidates(data.candidates)
    } catch (e) {
      console.error('후보 로드 실패:', e)
    } finally {
      setLoading(false)
    }
  }

  const handleSwipe = async (direction) => {
    const candidate = candidates[currentIdx]
    if (!candidate) return

    // 카드 날리기 애니메이션
    const card = cardRef.current
    if (card) {
      card.style.setProperty('--slide-x', direction === 'like' ? '120%' : '-120%')
      card.classList.add('slide-out')
    }

    if (direction === 'like') {
      // like → 바로 매칭 팝업 → 대화방 열기
      setTimeout(() => setShowMatch(candidate), 400)
    }

    // 스와이프 기록 (선택적)
    try {
      const username = user?.username || user?.id
      await fetch(`${API_BASE}/match/swipe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Username': username },
        credentials: 'include',
        body: JSON.stringify({ targetId: candidate.id, direction })
      })
    } catch (e) {
      console.error('스와이프 기록 실패:', e)
    }

    setTimeout(() => {
      setCurrentIdx(prev => prev + 1)
      setDragOffset(0)
    }, 400)
  }

  // 대화방 열기
  const handleStartChat = async (candidate) => {
    try {
      const username = user?.username || user?.id
      const res = await fetch(`${API_BASE}/messages/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Username': username },
        credentials: 'include',
        body: JSON.stringify({ participantUsername: candidate.username })
      })
      const data = await res.json()
      if (data.success && onStartChat) {
        onStartChat(data.conversation)
      }
      setShowMatch(null)
    } catch (e) {
      console.error('대화방 생성 실패:', e)
    }
  }

  // 터치/마우스 드래그
  const handleDragStart = (e) => {
    setIsDragging(true)
    startX.current = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX
  }

  const handleDragMove = (e) => {
    if (!isDragging) return
    const x = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX
    setDragOffset(x - startX.current)
  }

  const handleDragEnd = () => {
    if (!isDragging) return
    setIsDragging(false)
    
    if (dragOffset > 100) handleSwipe('like')
    else if (dragOffset < -100) handleSwipe('pass')
    else setDragOffset(0)
  }

  const current = candidates[currentIdx]

  if (loading) {
    return (
      <Container theme={theme}>
        <EmptyState theme={theme}>
          <Sparkles size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
          <h3>매칭 상대를 찾는 중...</h3>
        </EmptyState>
      </Container>
    )
  }

  return (
    <Container theme={theme}>
      <Header>
        <BackBtn theme={theme} onClick={onClose}><ArrowLeft size={20} /></BackBtn>
        <Title theme={theme}><Sparkles size={20} /> 새로운 사람 만나기</Title>
      </Header>

      {!current ? (
        <EmptyState theme={theme}>
          <Heart size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
          <h3>모든 프로필을 확인했어요!</h3>
          <p>새로운 사람들이 들어오면 다시 알려드릴게요 ✨</p>
        </EmptyState>
      ) : (
        <>
          <CardWrapper>
            <Card 
              ref={cardRef}
              theme={theme}
              className={isDragging ? 'swiping' : ''}
              style={{ transform: `translateX(${dragOffset}px) rotate(${dragOffset * 0.05}deg)` }}
              onMouseDown={handleDragStart}
              onMouseMove={handleDragMove}
              onMouseUp={handleDragEnd}
              onMouseLeave={() => isDragging && handleDragEnd()}
              onTouchStart={handleDragStart}
              onTouchMove={handleDragMove}
              onTouchEnd={handleDragEnd}
            >
              {dragOffset !== 0 && (
                <>
                  {dragOffset > 30 && <SwipeIndicator theme={theme} side="right" offset={dragOffset}>LIKE</SwipeIndicator>}
                  {dragOffset < -30 && <SwipeIndicator theme={theme} side="left" offset={dragOffset}>NOPE</SwipeIndicator>}
                </>
              )}
              <CardImage bg={current.avatar_url}>
                <CardInfo>
                  <CardName>{current.full_name || current.username}</CardName>
                  <CardBio>{current.bio || '안녕하세요! 만나서 반가워요 👋'}</CardBio>
                </CardInfo>
              </CardImage>
              <CardStats theme={theme}>
                <Stat theme={theme}>게시물 <span>{current.post_count || 0}</span></Stat>
                <Stat theme={theme}>팔로워 <span>{current.follower_count || 0}</span></Stat>
              </CardStats>
            </Card>
          </CardWrapper>

          <ActionBar>
            <ActionBtn theme={theme} color="#ff4458" onClick={() => handleSwipe('pass')}>
              <X size={28} />
            </ActionBtn>
            <ActionBtn theme={theme} color="#00d46a" onClick={() => handleSwipe('like')}>
              <Heart size={28} />
            </ActionBtn>
          </ActionBar>
        </>
      )}

      {showMatch && (
        <MatchOverlay onClick={() => setShowMatch(null)}>
          <MatchContent onClick={e => e.stopPropagation()}>
            <MatchTitle>💬 대화를 시작해보세요!</MatchTitle>
            <MatchSubtitle>{showMatch.full_name || showMatch.username}님과 대화할 수 있어요</MatchSubtitle>
            <MatchAvatars>
              <MatchAvatar bg={user?.avatar_url}>
                {!user?.avatar_url && <User size={32} />}
              </MatchAvatar>
              <Heart size={24} color="#ff6b6b" />
              <MatchAvatar bg={showMatch.avatar_url}>
                {!showMatch.avatar_url && <User size={32} />}
              </MatchAvatar>
            </MatchAvatars>
            <MatchBtn style={{ background: '#2563eb', color: 'white' }} onClick={() => handleStartChat(showMatch)}>
              <MessageSquare size={16} style={{ marginRight: 8, verticalAlign: 'text-bottom' }} />
              대화 시작하기
            </MatchBtn>
            <MatchBtn style={{ background: 'rgba(255,255,255,0.1)', color: 'white' }} onClick={() => setShowMatch(null)}>
              계속 둘러보기
            </MatchBtn>
          </MatchContent>
        </MatchOverlay>
      )}
    </Container>
  )
}
